import { useState } from "react";
import { tagsApi } from "@services/api/tags";
import { useTagStore } from "@store/tagStore";
import { useDataStore } from "@store/user_org";
import { TAG_COLOR_PALETTE } from "@utils/tagColor";
import { showNotification } from "context/notification.service";

interface TagCreateInlineProps {
  scope: string;
  /** Appelé avec l'id du tag créé — ou retrouvé, si un tag du même nom existait déjà. */
  onCreated: (tagId: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

/**
 * Création d'un tag sans quitter l'écran : un nom, Entrée, c'est posé.
 * La couleur est prise dans la palette (la première encore libre pour ce scope) ;
 * elle reste modifiable dans Réglages → Tags. Un nom déjà pris réutilise le tag
 * existant plutôt que d'en créer un doublon.
 */
export default function TagCreateInline({ scope, onCreated, placeholder = "Nouveau tag…", autoFocus }: TagCreateInlineProps) {
  const orgId = useDataStore((s) => s.orgData?.id);
  const tags = useTagStore((s) => s.tags);
  const addTag = useTagStore((s) => s.addTag);
  const updateTag = useTagStore((s) => s.updateTag);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || !orgId || saving) return;
    const existing = tags.find((t) => t.scope === scope && t.name.toLowerCase() === trimmed.toLowerCase());
    setSaving(true);
    try {
      if (existing) {
        if (!existing.active) updateTag(await tagsApi.update(existing.id, { active: true }));
        onCreated(existing.id);
        setName("");
        return;
      }
      const used = new Set(tags.filter((t) => t.scope === scope).map((t) => t.color.toUpperCase()));
      const color = TAG_COLOR_PALETTE.find((c) => !used.has(c.toUpperCase()))
        || TAG_COLOR_PALETTE[tags.length % TAG_COLOR_PALETTE.length];
      const created = await tagsApi.create({ org_id: orgId, name: trimmed, color, scope, active: true });
      addTag(created);
      onCreated(created.id);
      setName("");
    } catch {
      showNotification({ type: "error", message: "Impossible de créer le tag" });
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = !!name.trim() && !saving;

  return (
    <div className="flex flex-row items-center gap-1 w-full" onClick={(e) => e.stopPropagation()}>
      <input
        className="flex-1 min-w-0 text-h4 rounded-md px-2 py-1"
        style={{ border: "1px solid #E5E5E5", outline: "none", fontFamily: "Poppins", color: "#374151" }}
        placeholder={placeholder}
        value={name}
        autoFocus={autoFocus}
        disabled={saving}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); submit(); }
          if (e.key === "Escape") { e.stopPropagation(); setName(""); }
        }}
      />
      <button
        type="button"
        className="text-h4 font-medium rounded-md px-2 py-1"
        style={{
          background: canSubmit ? "#1A1A1A" : "#E5E5E5",
          color: canSubmit ? "#FFF" : "#A1A1A1",
          cursor: canSubmit ? "pointer" : "default",
          fontFamily: "Poppins",
          whiteSpace: "nowrap",
        }}
        disabled={!canSubmit}
        onClick={submit}
      >
        {saving ? "…" : "Créer"}
      </button>
    </div>
  );
}
